/**
 * Sign in by handing off to the system browser.
 *
 * The Word taskpane can't run OAuth in its own webview reliably, so instead of
 * embedding Microsoft/Google here we send the user to the **web app's** login —
 * which already has working Microsoft, Google, and password sign-in — in their
 * real browser (where they're usually already signed in). The add-in generates a
 * one-time `nonce`, opens the web login carrying it, and then polls the backend
 * until the web app has parked a session under that nonce. What comes back is
 * this application's own session, stored exactly like a password login.
 *
 * The add-in needs no client IDs, no MSAL, and no identity-provider config — the
 * browser and the web app own all of that.
 */

import { redeemAuthHandoff } from "../api/client";
import { openExternal } from "../variant-c/services/openExternal";

/** Where the browser is sent to sign in. One URL — not a set of client IDs. */
const WEB_LOGIN_URL =
  (import.meta.env.VITE_WEB_LOGIN_URL as string | undefined)?.trim() || "https://app.sangnan.ai/web/login";

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 180_000; // 3 minutes to complete sign-in in the browser

/** The user closed/abandoned the browser sign-in. Shown quietly, not as an error. */
export class SsoCancelledError extends Error {
  constructor() {
    super("Sign-in cancelled.");
    this.name = "SsoCancelledError";
  }
}

/** No session arrived within the window. */
export class SsoTimeoutError extends Error {
  constructor() {
    super("Sign-in timed out. Please try again.");
    this.name = "SsoTimeoutError";
  }
}

/** 32 random bytes as hex (256 bits) — the capability the whole flow hinges on. */
function makeNonce(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

const sleep = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new SsoCancelledError());
      return;
    }
    const id = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(id);
      reject(new SsoCancelledError());
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });

/**
 * Open the browser to sign in and resolve once the session comes back.
 *
 * Rejects with `SsoCancelledError` if `signal` aborts (the user pressed Cancel)
 * and `SsoTimeoutError` if nothing arrives in time. On success the session is
 * already stored by `redeemAuthHandoff`; the username is returned for the caller
 * to hand to `onLoginSuccess`.
 */
export async function signInViaBrowser(signal?: AbortSignal): Promise<{ username: string }> {
  const nonce = makeNonce();
  const url = `${WEB_LOGIN_URL}${WEB_LOGIN_URL.includes("?") ? "&" : "?"}addin_handoff=${nonce}`;
  openExternal(url);

  const deadline = Date.now() + POLL_TIMEOUT_MS;
  while (Date.now() < deadline) {
    if (signal?.aborted) throw new SsoCancelledError();
    try {
      const session = await redeemAuthHandoff(nonce);
      if (session !== null) return session;
    } catch {
      // A single failed poll must not end the whole flow: the user may still be
      // signing in in the browser, and the backend can blip mid-wait (a deploy,
      // a dev-server reload, a dropped request). Swallow it and keep polling
      // until the deadline — a persistent failure surfaces as a timeout.
    }
    await sleep(POLL_INTERVAL_MS, signal);
  }
  throw new SsoTimeoutError();
}
